import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkles, Loader2, Trash2, ChevronLeft, ChevronRight, Plus, Flag, CalendarClock, Lock,
  BellRing, AlertTriangle, Plane, Wand2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UpgradeGate } from "@/components/UpgradeGate";
import { DailyLedger } from "@/components/DailyLedger";
import { FileDropzone } from "@/components/FileDropzone";
import { ensureNotifyPermission, notify } from "@/lib/notify";

const EXAMS = [
  "AP Calculus AB", "AP Calculus BC", "AP Statistics",
  "AP Macroeconomics", "AP Microeconomics",
  "AP Biology", "AP Chemistry", "AP Physics 1", "AP Physics 2", "AP Physics C", "AP Environmental Science",
  "AP Psychology",
  "AP US History", "AP World History", "AP European History", "AP US Government",
  "AP English Language", "AP English Literature",
  "AP Computer Science A", "AP Human Geography",
  "SAT", "SAT Math", "SAT Reading & Writing",
  "ACT", "ACT English", "ACT Math", "ACT Reading", "ACT Science",
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["S","M","T","W","T","F","S"];
const todayISO = () => new Date().toISOString().slice(0, 10);

export function SyllabusArchitect({ onAuth, onNavigate }) {
  const { user } = useAuth();
  const [deadlines, setDeadlines] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [genOpen, setGenOpen] = useState(true);

  // generator form
  const [gExam, setGExam] = useState("AP Calculus BC");
  const [gDate, setGDate] = useState("");
  const [gFocus, setGFocus] = useState("");
  const [gText, setGText] = useState("");
  const [generating, setGenerating] = useState(false);

  // manual form
  const [mTitle, setMTitle] = useState("");
  const [mExam, setMExam] = useState("SAT");
  const [mDate, setMDate] = useState("");
  const [mPriority, setMPriority] = useState("normal");
  const [extractedText, setExtractedText] = useState("");

  // Trip Mode / conversational re-balancing
  const [tripInstruction, setTripInstruction] = useState("");
  const [rebalancing, setRebalancing] = useState(false);
  const [roadmap, setRoadmap] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/deadlines");
      setDeadlines(data);
    } catch (err) {
      // Expected 401 before auth resolves; log anything else.
      if (err?.response?.status !== 401) console.error("Failed to load deadlines", err);
    }
  };

  const rebalance = async (e) => {
    e.preventDefault();
    if (!tripInstruction.trim()) return toast.error("Tell me what changed, e.g. 'trip until Monday'.");
    setRebalancing(true);
    try {
      const { data } = await api.post("/syllabus/rebalance", { instruction: tripInstruction });
      setRoadmap(data.roadmap);
      setTripInstruction("");
      await load();
      notify("Schedule re-balanced", data.roadmap);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRebalancing(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // load is stable for this component; re-run only when the user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Motivational nudges + missed-task alerts (browser notification w/ toast fallback).
  const nudgedRef = useRef(false);
  useEffect(() => {
    if (!user || nudgedRef.current) return;
    nudgedRef.current = true;
    ensureNotifyPermission();
    api.get("/motivation/nudge").then(({ data }) => {
      if (data.nudge) notify(data.nudge.title, data.nudge.body);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const missedAlertedRef = useRef(false);
  useEffect(() => {
    if (missedAlertedRef.current || deadlines.length === 0) return;
    const today = todayISO();
    const overdue = deadlines.filter((d) => !d.done && d.date < today);
    if (overdue.length > 0) {
      missedAlertedRef.current = true;
      notify(
        `${overdue.length} task${overdue.length > 1 ? "s" : ""} need catching up`,
        `"${overdue[0].title}" is past due. Tell the planner about any schedule change to re-balance.`,
        { kind: "warning" }
      );
    }
  }, [deadlines]);

  const generate = async (e) => {
    e.preventDefault();
    if (!gDate) return toast.error("Choose an exam date first.");
    setGenerating(true);
    try {
      const { data } = await api.post("/syllabus/generate", {
        exam: gExam, exam_date: gDate, focus: gFocus, syllabus_text: gText,
      });
      toast.success(`Architected ${data.milestones.length} milestones.`);
      await load();
      setGText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!mTitle || !mDate) return toast.error("Title and date are required.");
    try {
      await api.post("/deadlines", { title: mTitle, exam: mExam, date: mDate, priority: mPriority });
      toast.success("Deadline added.");
      setMTitle(""); setMDate("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggle = async (id) => {
    try { await api.patch(`/deadlines/${id}`); await load(); }
    catch (err) { console.error("Failed to toggle deadline", err); toast.error("Could not update the deadline."); }
  };
  const remove = async (id) => {
    try { await api.delete(`/deadlines/${id}`); await load(); }
    catch (err) { console.error("Failed to delete deadline", err); toast.error("Could not delete the deadline."); }
  };

  const byDate = useMemo(() => {
    const m = {};
    deadlines.forEach((d) => { (m[d.date] ||= []).push(d); });
    return m;
  }, [deadlines]);

  const reminders = useMemo(() => {
    const today = todayISO();
    const soonMax = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
    const open = deadlines.filter((d) => !d.done);
    return {
      overdue: open.filter((d) => d.date < today).sort((a, b) => a.date.localeCompare(b.date)),
      soon: open.filter((d) => d.date >= today && d.date <= soonMax).sort((a, b) => a.date.localeCompare(b.date)),
    };
  }, [deadlines]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      cells.push(`${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    return cells;
  }, [cursor]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="h-14 w-14 rounded-xl bg-navy-soft flex items-center justify-center mx-auto mb-6">
          <Lock className="h-6 w-6 text-navy" />
        </div>
        <h2 className="font-serif text-3xl font-semibold text-navy">Sign in to architect your syllabus</h2>
        <p className="text-slate-600 mt-3">Your study milestones and calendar are private to your account.</p>
        <Button onClick={() => onAuth("login")} data-testid="syllabus-auth-cta"
          className="mt-6 bg-navy hover:bg-navy-light text-white">Sign in / Enrol</Button>
      </div>
    );
  }

  if (!user.premium && user.role !== "admin") {
    return <UpgradeGate feature="the AI Syllabus Architect" onUpgrade={() => onNavigate("home")} />;
  }

  const sorted = [...deadlines].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">Planner</span>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-2">AI Syllabus Architect &amp; Deadline Planner</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Generate an escalating study schedule from an exam target, or track deadlines by hand. Persian-blue dates carry a milestone; rose flags mark high-priority goals.</p>
      </div>

      {(reminders.overdue.length > 0 || reminders.soon.length > 0) && (
        <div data-testid="reminders-banner" className="mb-8 rounded-2xl border border-persian-rose/30 bg-persian-rose/[0.06] p-5">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="h-5 w-5 text-persian-rose" />
            <h3 className="font-serif text-lg text-navy">Study reminders</h3>
            <span className="text-xs text-slate-500">a nudge so nothing slips</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {reminders.overdue.map((d) => (
              <span key={d.id} data-testid={`reminder-overdue-${d.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-persian-rose/15 text-persian-rose border border-persian-rose/30 rounded-full px-3 py-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Overdue · {d.title}
              </span>
            ))}
            {reminders.soon.map((d) => (
              <span key={d.id} data-testid={`reminder-soon-${d.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-persian-blue/10 text-navy border border-persian-blue/30 rounded-full px-3 py-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-persian-blue" />
                {d.title} · {new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            ))}
          </div>
        </div>
      )}

      <DailyLedger context={extractedText} />

      {/* Trip Mode — conversational calendar re-balancing */}
      <div className="mb-8 rounded-2xl border border-persian-blue/30 bg-navy p-5 text-white" data-testid="trip-mode-card">
        <div className="flex items-center gap-2 mb-1">
          <Plane className="h-5 w-5 text-persian-blue" />
          <h3 className="font-serif text-lg">Life happens — re-balance your plan</h3>
        </div>
        <p className="text-sm text-slate-300 mb-3">
          Tell the planner in plain words: "I'm on a trip until Monday" or "I need more time on macroeconomics."
          It instantly shifts milestones and hands you a recovery roadmap.
        </p>
        <form onSubmit={rebalance} className="flex flex-col sm:flex-row gap-2">
          <Input value={tripInstruction} onChange={(e) => setTripInstruction(e.target.value)}
            data-testid="trip-mode-input" placeholder="e.g. I'm away until Monday — catch me up"
            className="bg-white/10 border-white/20 text-white placeholder:text-slate-400" />
          <Button type="submit" disabled={rebalancing} data-testid="trip-mode-submit"
            className="bg-persian-blue hover:bg-persian-bluehover text-navy font-semibold shrink-0">
            {rebalancing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Re-balancing…</> : <><Wand2 className="h-4 w-4 mr-2" />Re-balance</>}
          </Button>
        </form>
        {roadmap && (
          <div className="mt-3 rounded-xl bg-persian-blue/10 border border-persian-blue/30 p-3 text-sm text-slate-100"
            data-testid="trip-mode-roadmap">
            {roadmap}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: generator + manual */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-navy rounded-2xl p-6 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-persian-blue" />
              <h3 className="font-serif text-xl">Generate a study plan</h3>
            </div>
            <form onSubmit={generate} className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Exam</Label>
                <Select value={gExam} onValueChange={setGExam}>
                  <SelectTrigger data-testid="gen-exam-select" className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXAMS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Exam date</Label>
                <Input type="date" min={todayISO()} value={gDate} onChange={(e) => setGDate(e.target.value)}
                  data-testid="gen-date-input" className="bg-white/10 border-white/20 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Focus emphasis (optional)</Label>
                <Input value={gFocus} onChange={(e) => setGFocus(e.target.value)} placeholder="e.g. integration techniques, elasticity"
                  data-testid="gen-focus-input" className="bg-white/10 border-white/20 text-white placeholder:text-slate-400" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Upload a syllabus (PDF, PNG, JPEG)</Label>
                <FileDropzone onExtracted={(text) => {
                  setExtractedText(text);
                  setGText((prev) => (prev ? prev + "\n\n" : "") + text);
                }} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Paste syllabus text (optional)</Label>
                <Textarea value={gText} onChange={(e) => setGText(e.target.value)} rows={3}
                  placeholder="Paste units / topics and the architect will align milestones to them."
                  data-testid="gen-syllabus-textarea" className="bg-white/10 border-white/20 text-white placeholder:text-slate-400" />
              </div>
              <Button type="submit" disabled={generating} data-testid="gen-submit-button"
                className="w-full bg-persian-blue hover:bg-persian-bluehover text-navy font-semibold">
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Architecting…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate schedule</>}
              </Button>
            </form>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-navy" />
              <h3 className="font-serif text-xl text-navy">Add a deadline manually</h3>
            </div>
            <form onSubmit={addManual} className="mt-4 space-y-3">
              <Input value={mTitle} onChange={(e) => setMTitle(e.target.value)} placeholder="Milestone title"
                data-testid="manual-title-input" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={mExam} onValueChange={setMExam}>
                  <SelectTrigger data-testid="manual-exam-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXAMS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="date" min={todayISO()} value={mDate} onChange={(e) => setMDate(e.target.value)}
                  data-testid="manual-date-input" />
              </div>
              <div className="flex items-center gap-3">
                <Select value={mPriority} onValueChange={setMPriority}>
                  <SelectTrigger data-testid="manual-priority-select" className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High priority</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" data-testid="add-exam-deadline-button"
                  className="flex-1 bg-navy hover:bg-navy-light text-white">Add deadline</Button>
              </div>
            </form>
          </div>
        </div>

        {/* Right: calendar + list */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-navy">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" data-testid="cal-prev"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" data-testid="cal-next"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {DOW.map((d, i) => <div key={i} className="text-[11px] font-mono uppercase tracking-wider text-slate-400 py-1">{d}</div>)}
              {grid.map((iso, i) => {
                if (!iso) return <div key={i} />;
                const items = byDate[iso] || [];
                const hasHigh = items.some((x) => x.priority === "high");
                const isToday = iso === todayISO();
                return (
                  <div key={i} data-testid={items.length ? `cal-day-active-${iso}` : undefined}
                    className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-colors ${
                      items.length ? "bg-persian-blue/10 ring-1 ring-persian-blue text-navy font-semibold" : "text-slate-600 hover:bg-navy-soft"
                    } ${isToday ? "outline outline-1 outline-navy" : ""}`}>
                    <span>{parseInt(iso.slice(-2), 10)}</span>
                    {items.length > 0 && (
                      <span className={`mt-0.5 h-1.5 w-1.5 rounded-full ${hasHigh ? "bg-persian-rose" : "bg-persian-blue"}`} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-5 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-persian-blue" /> Milestone</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-persian-rose" /> High priority</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-navy flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Upcoming milestones</h3>
              <span className="text-xs font-mono text-slate-400">{sorted.length} total</span>
            </div>
            {sorted.length === 0 ? (
              <p data-testid="deadlines-empty" className="text-sm text-slate-500 py-8 text-center">No milestones yet. Generate a plan or add one manually.</p>
            ) : (
              <ul className="space-y-2" data-testid="deadlines-list">
                {sorted.map((d) => (
                  <motion.li key={d.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                    data-testid={`deadline-item-${d.id}`}
                    className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${
                      d.done ? "border-slate-100 bg-slate-50/70" : "border-slate-200"
                    }`}>
                    <Checkbox checked={d.done} onCheckedChange={() => toggle(d.id)}
                      data-testid={`deadline-toggle-${d.id}`} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${d.done ? "line-through text-slate-400" : "text-navy"}`}>{d.title}</span>
                        {d.priority === "high" && !d.done && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-persian-rose bg-persian-rose/10 px-2 py-0.5 rounded-full">
                            <Flag className="h-3 w-3" /> Priority
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {d.exam} · {new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                      {d.notes && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{d.notes}</p>}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid={`deadline-delete-${d.id}`}
                      className="text-slate-400 hover:text-persian-rose h-8 w-8">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
